import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Calendar } from 'lucide-react';
import { apiUrl } from '../utils/api';

interface ProductItem {
  id?: string;
  name: string;
  totalNeeded: number;
  unit: string;
  description?: string;
}

interface FormData {
  title: string;
  description: string;
  deadline: string;
  city: string;
  address: string;
  phone: string;
  email: string;
}

interface Campaign {
  id: string;
  title: string;
  description: string;
  deadline: string;
  location: {
    city: string;
    state: string;
    address: string;
  };
  contactInfo: {
    phone: string;
    email: string;
  };
  privacyMode: 'public' | 'private';
  status: 'active' | 'expired' | 'completed' | 'cancelled';
}

export function EditCampaignPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    deadline: '',
    city: '',
    address: '',
    phone: '',
    email: '',
  });

  const [products, setProducts] = useState<ProductItem[]>([
    { name: '', totalNeeded: 1, unit: '', description: '' }
  ]);

  // Ref for auto-scroll
  const productsEndRef = useRef<HTMLDivElement>(null);

  // Load campaign data on mount
  useEffect(() => {
    if (id) {
      loadCampaignData();
    }
  }, [id]);

  // Store original products for comparison
  const [originalProducts, setOriginalProducts] = useState<ProductItem[]>([]);

  const loadCampaignData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      console.log('🔍 Loading campaign data for ID:', id);

      // Load campaign details
      const campaignResponse = await fetch(apiUrl(`/api/campaigns/${id}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const campaignData = await campaignResponse.json();
      console.log('📋 Campaign response:', campaignData);

      if (campaignData.success) {
        const campaign: Campaign = campaignData.data;
        
        console.log('✅ Campaign loaded:', campaign.title);
        
        // Populate form data
        setFormData({
          title: campaign.title,
          description: campaign.description,
          deadline: new Date(campaign.deadline).toISOString().split('T')[0],
          city: campaign.location.city,
          address: campaign.location.address,
          phone: campaign.contactInfo.phone,
          email: campaign.contactInfo.email,
        });

        // Load cover image if exists - DISABLED
        // if ((campaign as any).coverImageUrl) {
        //   setCurrentCoverImageUrl((campaign as any).coverImageUrl);
        // }

        // Load products
        console.log('🛍️ Loading products for campaign:', id);
        const productsResponse = await fetch(apiUrl(`/api/products/campaign/${id}`));
        const productsData = await productsResponse.json();

        console.log('🛍️ Products response:', productsData); // Debug log

        if (productsData.success && productsData.data.products && productsData.data.products.length > 0) {
          const loadedProducts = productsData.data.products.map((product: any) => ({
            id: product.id,
            name: product.name,
            totalNeeded: product.totalNeeded,
            unit: product.unit,
            description: product.description || '',
          }));
          
          console.log('🛍️ Mapped products:', loadedProducts); // Debug log
          setProducts(loadedProducts);
          setOriginalProducts(loadedProducts); // Store original for comparison
        } else {
          console.log('⚠️ No products found, using default empty product'); // Debug log
          // Keep the default empty product for new additions
          setProducts([{ name: '', totalNeeded: 1, unit: '', description: '' }]);
          setOriginalProducts([]); // No original products
        }
      } else {
        console.error('❌ Failed to load campaign:', campaignData.error);
        setError('No se pudo cargar la campaña. Verifica que tengas permisos para editarla.');
      }
    } catch (err) {
      console.error('💥 Error loading campaign:', err);
      setError('Error al cargar la campaña. Verifica tu conexión a internet.');
    } finally {
      setIsLoadingCampaign(false);
    }
  };

  // Input handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    if (error) setError('');
  };

  // Product handlers
  const handleProductChange = (index: number, field: keyof ProductItem, value: string | number) => {
    const updatedProducts = [...products];
    updatedProducts[index] = {
      ...updatedProducts[index],
      [field]: value,
    };
    setProducts(updatedProducts);
  };

  const addProduct = () => {
    setProducts(prev => [...prev, { name: '', totalNeeded: 1, unit: '', description: '' }]);
    
    setTimeout(() => {
      if (productsEndRef.current) {
        productsEndRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
  };

  const removeProduct = (index: number) => {
    if (products.length > 1) {
      setProducts(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Form validation
  const isFormValid = () => {
    const hasTitle = formData.title.trim().length > 0;
    const hasDescription = formData.description.trim().length > 0;
    const hasDeadline = formData.deadline.length > 0;
    const hasCity = formData.city.trim().length > 0;
    const hasAddress = formData.address.trim().length > 0;
    const hasPhone = formData.phone.trim().length >= 10;
    const hasEmail = formData.email.trim().includes('@');
    const hasValidProducts = products.filter(p => p.name.trim() && p.unit.trim() && p.totalNeeded > 0).length > 0;
    
    return hasTitle && hasDescription && hasDeadline && hasCity && hasAddress && hasPhone && hasEmail && hasValidProducts;
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (!isFormValid()) {
      setError('Por favor, completa todos los campos requeridos.');
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const deadlineDate = new Date(formData.deadline);

      // Update campaign data
      const campaignData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        deadline: deadlineDate.toISOString(),
        location: {
          country: 'Colombia',
          state: formData.city.trim(),
          city: formData.city.trim(),
          address: formData.address.trim(),
        },
        contactInfo: {
          phone: formData.phone.trim(),
          email: formData.email.trim(),
        },
        privacyMode: 'private' as const,
        campaignType: 'solidarity' as const,
      };

      let campaignResponse;

      // Use JSON request (cover image functionality disabled)
      campaignResponse = await fetch(apiUrl(`/api/campaigns/${id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(campaignData),
      });

      const campaignResult = await campaignResponse.json();

      if (campaignResult.success) {
        // Handle products update
        const validProducts = products.filter(p => p.name.trim() && p.unit.trim() && p.totalNeeded > 0);
        
        console.log('🛍️ Processing products:', validProducts); // Debug log

        // Separate existing products from new ones
        const existingProducts = validProducts.filter(p => p.id);
        const newProducts = validProducts.filter(p => !p.id);

        // Find products that were deleted (exist in original but not in current)
        const deletedProducts = originalProducts.filter(original => 
          !validProducts.some(current => current.id === original.id)
        );

        console.log('🔄 Existing products:', existingProducts.length);
        console.log('➕ New products:', newProducts.length);
        console.log('🗑️ Deleted products:', deletedProducts.length);

        // Delete removed products
        for (const product of deletedProducts) {
          try {
            const deleteResponse = await fetch(apiUrl(`/api/products/${product.id}`), {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            const deleteResult = await deleteResponse.json();
            if (!deleteResult.success) {
              console.warn('⚠️ Failed to delete product:', product.id, deleteResult.error);
            } else {
              console.log('🗑️ Deleted product:', product.name);
            }
          } catch (err) {
            console.error('❌ Error deleting product:', product.id, err);
          }
        }

        // Update existing products
        for (const product of existingProducts) {
          try {
            const updateResponse = await fetch(apiUrl(`/api/products/${product.id}`), {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                name: product.name.trim(),
                totalNeeded: product.totalNeeded,
                unit: product.unit.trim(),
                description: product.description?.trim() || undefined,
              }),
            });

            const updateResult = await updateResponse.json();
            if (!updateResult.success) {
              console.warn('⚠️ Failed to update product:', product.id, updateResult.error);
            } else {
              console.log('🔄 Updated product:', product.name);
            }
          } catch (err) {
            console.error('❌ Error updating product:', product.id, err);
          }
        }

        // Create new products
        for (const product of newProducts) {
          try {
            const createResponse = await fetch(apiUrl('/api/products'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                campaignId: id,
                name: product.name.trim(),
                totalNeeded: product.totalNeeded,
                unit: product.unit.trim(),
                description: product.description?.trim() || undefined,
              }),
            });

            const createResult = await createResponse.json();
            if (!createResult.success) {
              console.warn('⚠️ Failed to create product:', createResult.error);
            } else {
              console.log('➕ Created product:', product.name);
            }
          } catch (err) {
            console.error('❌ Error creating product:', err);
          }
        }

        console.log('✅ Campaign and products updated successfully');
        
        // Invalidate dashboard cache
        if ((window as any).refreshDashboardCampaigns) {
          console.log('🔄 Invalidating dashboard cache...');
          (window as any).refreshDashboardCampaigns();
        }
        
        navigate('/dashboard', { 
          state: { message: 'Campaña actualizada exitosamente' }
        });
      } else {
        setError(campaignResult.error?.message || 'Error al actualizar la campaña');
      }
    } catch (err) {
      console.error('Error updating campaign:', err);
      setError('Error de conexión. Por favor, intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  if (isLoadingCampaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando campaña...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Editar Campaña</h1>
              <p className="text-gray-600">Actualiza la información de tu campaña</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          )}

          {/* Form Status */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-800 mb-3">
              <strong>Editando Campaña:</strong> Actualiza los campos que necesites modificar
              {isLoadingCampaign && <span className="ml-2">⏳ Cargando datos...</span>}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className={`flex items-center ${formData.title.trim() ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{formData.title.trim() ? '✅' : '❌'}</span>
                <span>Título {formData.title.trim() ? '✓' : '(requerido)'}</span>
              </div>
              <div className={`flex items-center ${formData.description.trim() ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{formData.description.trim() ? '✅' : '❌'}</span>
                <span>Descripción {formData.description.trim() ? '✓' : '(requerida)'}</span>
              </div>
              <div className={`flex items-center ${formData.deadline ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{formData.deadline ? '✅' : '❌'}</span>
                <span>Fecha {formData.deadline ? '✓' : '(requerida)'}</span>
              </div>
              <div className={`flex items-center ${formData.city.trim() && formData.address.trim() ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{formData.city.trim() && formData.address.trim() ? '✅' : '❌'}</span>
                <span>Ubicación {formData.city.trim() && formData.address.trim() ? '✓' : '(requerida)'}</span>
              </div>
              <div className={`flex items-center ${formData.phone.trim().length >= 10 ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{formData.phone.trim().length >= 10 ? '✅' : '❌'}</span>
                <span>Teléfono {formData.phone.trim().length >= 10 ? '✓' : `(${formData.phone.length}/10)`}</span>
              </div>
              <div className={`flex items-center ${formData.email.trim().includes('@') ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{formData.email.trim().includes('@') ? '✅' : '❌'}</span>
                <span>Email {formData.email.trim().includes('@') ? '✓' : '(formato inválido)'}</span>
              </div>
              <div className={`flex items-center ${products.filter(p => p.name.trim() && p.unit.trim()).length > 0 ? 'text-green-600' : 'text-red-500'}`}>
                <span className="mr-1">{products.filter(p => p.name.trim() && p.unit.trim()).length > 0 ? '✅' : '❌'}</span>
                <span>Productos ({products.filter(p => p.name.trim() && p.unit.trim()).length})</span>
              </div>
              <div className={`flex items-center ${isFormValid() ? 'text-green-600 font-bold' : 'text-orange-500'}`}>
                <span className="mr-1">{isFormValid() ? '🚀' : '⏳'}</span>
                <span>{isFormValid() ? 'LISTO PARA GUARDAR' : 'Completando...'}</span>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Información Básica</h2>
            
            <div className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Título de la Campaña *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Ayuda para familia damnificada"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción *
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe tu campaña, qué necesitas y por qué..."
                />
              </div>

              <div>
                <label htmlFor="deadline" className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Fecha Límite *
                </label>
                <input
                  type="date"
                  id="deadline"
                  name="deadline"
                  required
                  min={today}
                  value={formData.deadline}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Ubicación</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                  Ciudad *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  required
                  value={formData.city}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Cali, Medellín, Bogotá"
                />
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  Dirección *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  required
                  value={formData.address}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Calle 10 # 15-25, Barrio Centro"
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Información de Contacto</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: 300 123 4567"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="contacto@email.com"
                />
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900">Productos Necesarios</h2>
              <p className="text-sm text-gray-600 mt-1">Actualiza los productos que necesitas para tu campaña</p>
              <p className="text-xs text-yellow-600 mt-1">
                <strong>Nota:</strong> Los cambios en productos pueden afectar las donaciones existentes
              </p>
              {originalProducts.length > 0 && (
                <p className="text-xs text-green-600 mt-1">
                  ✅ Se cargaron {originalProducts.length} producto(s) existente(s)
                </p>
              )}
            </div>
            
            <div className="space-y-4">
              {products.map((product, index) => (
                <div key={index}>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-sm font-medium text-gray-700 flex items-center">
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full mr-2">
                          {index + 1}
                        </span>
                        Producto {index + 1}
                        {product.id && (
                          <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">Existente</span>
                        )}
                      </h3>
                      {products.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeProduct(index)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Eliminar producto"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Nombre del Producto *
                        </label>
                        <input
                          type="text"
                          value={product.name}
                          onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ej: Tarros de leche"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Cantidad *
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={product.totalNeeded}
                          onChange={(e) => handleProductChange(index, 'totalNeeded', parseInt(e.target.value) || 1)}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Unidad *
                        </label>
                        <input
                          type="text"
                          value={product.unit}
                          onChange={(e) => handleProductChange(index, 'unit', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ej: unidades, kg"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Descripción (opcional)
                      </label>
                      <input
                        type="text"
                        value={product.description || ''}
                        onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Ej: Leche entera de 1 litro"
                      />
                    </div>
                  </div>
                  
                  {/* Add Product Button after each product */}
                  <div className="flex justify-center mt-3">
                    <button
                      type="button"
                      onClick={addProduct}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Otro Producto
                    </button>
                  </div>
                </div>
              ))}
              
              {/* Reference for auto-scroll */}
              <div ref={productsEndRef} />
            </div>
          </div>

          {/* Submit Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="border-t border-gray-200 pt-6">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <p>¿Todo listo? Revisa tu información antes de guardar los cambios.</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Productos válidos: <strong>{products.filter(p => p.name.trim() && p.unit.trim()).length}</strong>
                  </p>
                </div>
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !isFormValid()}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                      isFormValid() && !isLoading 
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        Guardando...
                      </>
                    ) : (
                      '💾 Guardar Cambios'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}